import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, Clock, Loader2, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { logger } from "@/utils/logger";
import { useAuth } from "@/hooks/useAuth";
import { useAuthStore } from "@/hooks/useAuthStore";
import {
  getInviteDetailsFromToken,
  acceptInviteToken,
  type InviteDetails,
} from "@/utils/inviteUtils";
import { useRoleLabel } from "@/hooks/useRoleLabel";
import { track } from "@/lib/posthog";
import {
  AUTH_LINK_CLASSES,
  AUTH_PANEL_CLASSES,
  AUTH_PRIMARY_ACTION_CLASSES,
  AUTH_SECONDARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";
import { formatInvitationExpiry, getInvitationExpiryInfo } from "./invitationExpiry";

type PageState = "loading" | "invite_details" | "needs_signup" | "accepting" | "success" | "error";
type InviteErrorKind =
  | "missing_token"
  | "invalid_invitation"
  | "expired_invitation"
  | "accepted_invitation"
  | "inactive_invitation"
  | "load_failure"
  | "email_mismatch"
  | "accept_failure";

interface InviteStatePageProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  iconClassName?: string;
  children?: ReactNode;
}

function InviteStatePage({
  title,
  description,
  icon,
  iconClassName,
  children,
}: InviteStatePageProps) {
  return (
    <AuthPageLayout contentClassName="max-w-[440px]">
      <AuthPageHeader
        title={title}
        description={description}
        icon={icon}
        iconClassName={iconClassName}
      />
      {children}
    </AuthPageLayout>
  );
}

export default function InviteAccept() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { language, t } = useLanguage();
  const {
    emailMismatchError,
    failedToLoadInvitation,
    invalidOrExpiredInvitation,
    invitationAlreadyAccepted,
    invitationExpired,
    invitationNoLongerValid,
    noInvitationToken,
  } = t.auth;
  const roleLabel = useRoleLabel();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const authStore = useAuthStore();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorKind, setErrorKind] = useState<InviteErrorKind | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const token = searchParams.get("token");
  // Check if we came directly from signup - if so, we need to check session directly
  const fromSignup = (location.state as { fromSignup?: boolean })?.fromSignup;

  // Fetch user email when authenticated
  useEffect(() => {
    const fetchUserEmail = async () => {
      if (isAuthenticated) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setUserEmail(session?.user?.email?.toLowerCase().trim() || null);
      }
    };
    fetchUserEmail();
  }, [isAuthenticated]);

  // Fetch invite details when token is available
  useEffect(() => {
    const fetchInviteDetails = async () => {
      if (!token) {
        setErrorMessage(noInvitationToken);
        setErrorKind("missing_token");
        setPageState("error");
        return;
      }

      // Don't re-fetch once the page has settled on an outcome. `pageState` is a
      // dependency, so without "error" here a failed accept would immediately re-run
      // this fetch, find the invite still valid, and flip back to "invite_details" —
      // wiping the error before it ever renders.
      if (pageState === "accepting" || pageState === "success" || pageState === "error") {
        return;
      }

      // Wait for auth to finish loading (unless we came from signup)
      if (authLoading && !fromSignup) {
        return;
      }

      try {
        const details = await getInviteDetailsFromToken(token);

        if (!details) {
          setErrorMessage(invalidOrExpiredInvitation);
          setErrorKind("invalid_invitation");
          setPageState("error");
          return;
        }

        if (!details.isValid) {
          if (details.isExpired) {
            setErrorMessage(invitationExpired);
            setErrorKind("expired_invitation");
          } else if (details.status === "accepted") {
            setErrorMessage(invitationAlreadyAccepted);
            setErrorKind("accepted_invitation");
          } else {
            setErrorMessage(invitationNoLongerValid);
            setErrorKind("inactive_invitation");
          }
          setPageState("error");
          return;
        }

        setInviteDetails(details);
        setErrorKind(null);

        // Determine auth state - check Supabase session directly if we came from signup
        // because React context may not have updated yet
        let isUserAuthenticated = isAuthenticated;
        let currentUserEmail = userEmail;

        if (fromSignup || !isAuthenticated) {
          // Check Supabase session directly for the most up-to-date auth state
          const {
            data: { session },
          } = await supabase.auth.getSession();
          isUserAuthenticated = !!session;
          currentUserEmail = session?.user?.email?.toLowerCase().trim() || null;
        }

        // Check if user needs to sign up
        if (!isUserAuthenticated) {
          setPageState("needs_signup");
        } else {
          // Check if logged-in user's email matches invite email
          const inviteEmail = details.email.toLowerCase().trim();
          if (currentUserEmail && currentUserEmail !== inviteEmail) {
            setErrorMessage(
              emailMismatchError
                .replace("{inviteEmail}", details.email)
                .replace("{currentEmail}", currentUserEmail)
            );
            setErrorKind("email_mismatch");
            setPageState("error");
            return;
          }
          setPageState("invite_details");
        }
      } catch (error) {
        logger.error(
          "auth",
          "fetch-details-error",
          error instanceof Error ? error.message : "Unknown error"
        );
        setErrorMessage(failedToLoadInvitation);
        setErrorKind("load_failure");
        setPageState("error");
      }
    };

    fetchInviteDetails();
  }, [
    authLoading,
    emailMismatchError,
    failedToLoadInvitation,
    fromSignup,
    invalidOrExpiredInvitation,
    invitationAlreadyAccepted,
    invitationExpired,
    invitationNoLongerValid,
    isAuthenticated,
    noInvitationToken,
    pageState,
    token,
    userEmail,
  ]);

  const handleAcceptInvite = async () => {
    if (!token || !inviteDetails) return;

    setPageState("accepting");

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        throw new Error(t.auth.authenticationRequired);
      }

      const result = await acceptInviteToken(token, session.user.id);

      if (result.success) {
        track("invite_accepted", { role: inviteDetails.role, had_account: !fromSignup });
        setPageState("success");
        toast({
          title: t.auth.welcomeAboard,
          description: t.auth.successfullyJoinedOrg.replace(
            "{organizationName}",
            inviteDetails.organizationName
          ),
        });

        // Refresh session to update JWT with new org membership
        await supabase.auth.refreshSession();

        // Reload AuthStore to refetch org roles with updated JWT
        await authStore.loadSession();

        // Navigate to buildings after a short delay
        setTimeout(() => {
          navigate("/buildings", { replace: true });
        }, 2000);
      } else if (result.requiresSignup) {
        // Edge function says user needs to sign up first
        setPageState("needs_signup");
      } else {
        throw new Error(result.error || t.auth.failedToAcceptInvitation);
      }
    } catch (error) {
      logger.error(
        "auth",
        "accept-error",
        error instanceof Error ? error.message : "Unknown error"
      );
      setErrorMessage(error instanceof Error ? error.message : t.auth.failedToAcceptInvitation);
      setErrorKind("accept_failure");
      setPageState("error");
    }
  };

  const handleRetryInvitation = () => {
    if (errorKind === "accept_failure") {
      void handleAcceptInvite();
      return;
    }

    setErrorMessage("");
    setErrorKind(null);
    setPageState("loading");
  };

  const handleSignUpRedirect = () => {
    if (!token) return;
    // Redirect to signup with invite token preserved
    navigate(`/signup?invite_token=${encodeURIComponent(token)}`, { replace: true });
  };

  const invitationSummary = inviteDetails ? (
    <div className={AUTH_PANEL_CLASSES}>
      <dl className="divide-y">
        <div className="flex items-start justify-between gap-4 pb-4">
          <dt className="shrink-0 text-[13.5px] text-muted-foreground">
            {t.auth.invitationEmailLabel}
          </dt>
          <dd className="min-w-0 text-right">
            <span className="block break-words text-[13.5px] font-medium text-foreground [overflow-wrap:anywhere]">
              {inviteDetails.email}
            </span>
            <span className="mt-1 block text-xs leading-4 text-muted-foreground">
              {t.auth.invitationEmailHint}
            </span>
          </dd>
        </div>
        <div className="flex items-start justify-between gap-4 pt-4">
          <dt className="shrink-0 text-[13.5px] text-muted-foreground">
            {t.auth.invitationRoleLabel}
          </dt>
          <dd className="min-w-0 break-words text-right text-[13.5px] font-medium text-foreground [overflow-wrap:anywhere]">
            {roleLabel(inviteDetails.role)}
          </dd>
        </div>
      </dl>
    </div>
  ) : null;

  const invitationExpiry = inviteDetails
    ? formatInvitationExpiry(getInvitationExpiryInfo(inviteDetails.expiresAt, language), t.auth)
    : null;

  const invitationExpiryFooter = invitationExpiry ? (
    <p className="border-t pt-4 text-center text-xs leading-5 text-muted-foreground">
      {invitationExpiry}
    </p>
  ) : null;

  // Loading state
  if (pageState === "loading" || authLoading) {
    return (
      <InviteStatePage
        title={t.auth.loadingInvitation}
        description={t.auth.invitedToJoinOrg}
        icon={<Loader2 className="h-6 w-6 animate-spin" />}
      />
    );
  }

  // Error state
  if (pageState === "error") {
    // Special case: the user is verified and logged in, but the invite they just
    // verified their email for has expired. Their account is fine — they just
    // need a new invitation. Show dedicated copy + a path back to the dashboard.
    const isVerifiedWithExpiredInvite = isAuthenticated && errorKind === "expired_invitation";

    if (isVerifiedWithExpiredInvite) {
      return (
        <InviteStatePage
          title={t.auth.invitationExpired}
          description="Your Franklin account is active, but this invitation is no longer valid."
          icon={<Clock className="h-6 w-6" />}
          iconClassName="bg-amber-100 text-amber-600"
        >
          <div className="space-y-4">
            <Alert className="rounded-xl">
              <AlertDescription>
                Ask the person who invited you to send a new invitation, or contact support at{" "}
                <a href="mailto:hello@franklin.ee" className="font-semibold text-primary underline">
                  hello@franklin.ee
                </a>
                .
              </AlertDescription>
            </Alert>
            <div className="grid gap-3 sm:grid-cols-2">
              <Button asChild variant="default" className={AUTH_PRIMARY_ACTION_CLASSES}>
                <a href="mailto:hello@franklin.ee?subject=Expired%20invitation">Contact support</a>
              </Button>
              <Button variant="outline" asChild className={AUTH_SECONDARY_ACTION_CLASSES}>
                <Link to="/">Go to dashboard</Link>
              </Button>
            </div>
          </div>
        </InviteStatePage>
      );
    }

    const isRetryableError = errorKind === "load_failure" || errorKind === "accept_failure";
    const isAccountMismatch = errorKind === "email_mismatch";
    const backPath = isAuthenticated && !isAccountMismatch ? "/buildings" : "/login";
    const backLabel =
      isAuthenticated && !isAccountMismatch ? t.auth.goToDashboard : t.auth.goToLogin;

    return (
      <InviteStatePage
        title={t.auth.unableToProcessInvitation}
        description={
          isAccountMismatch
            ? t.auth.invitationAccountMismatchDescription
            : isRetryableError
              ? t.auth.invitationRetryDescription
              : t.auth.invitationErrorDescription
        }
      >
        <div className="space-y-5">
          <section className={`${AUTH_PANEL_CLASSES} space-y-4`}>
            <div className="flex items-start gap-3" role="alert" aria-live="assertive">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                <AlertCircle className="h-[18px] w-[18px]" aria-hidden />
              </span>
              <div className="min-w-0 pt-0.5">
                <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {t.auth.invitationError}
                </p>
                <p className="mt-1 text-[14px] leading-6 text-foreground">{errorMessage}</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-[14px] font-semibold text-foreground">
                {isAccountMismatch
                  ? t.auth.invitationAccountMismatchGuidanceTitle
                  : isRetryableError
                    ? t.auth.invitationRetryGuidanceTitle
                    : t.auth.invitationErrorGuidanceTitle}
              </h2>
              <p className="mt-1.5 text-[13.5px] leading-5 text-muted-foreground">
                {isAccountMismatch
                  ? t.auth.invitationAccountMismatchGuidance
                  : isRetryableError
                    ? t.auth.invitationRetryGuidance
                    : t.auth.invitationErrorGuidance}
              </p>
            </div>
          </section>

          <div className="space-y-3">
            {isRetryableError ? (
              <Button onClick={handleRetryInvitation} className={AUTH_PRIMARY_ACTION_CLASSES}>
                <RefreshCw aria-hidden />
                {t.common.tryAgain}
              </Button>
            ) : (
              <Button asChild className={AUTH_PRIMARY_ACTION_CLASSES}>
                <Link to={backPath}>{backLabel}</Link>
              </Button>
            )}

            {isRetryableError ? (
              <Button variant="outline" asChild className={AUTH_SECONDARY_ACTION_CLASSES}>
                <Link to={backPath}>{backLabel}</Link>
              </Button>
            ) : (
              <p className="text-center text-[13.5px] text-muted-foreground">
                <a
                  href="mailto:hello@franklin.ee?subject=Invitation%20support"
                  className={AUTH_LINK_CLASSES}
                >
                  {t.auth.contactSupport}
                </a>
              </p>
            )}
          </div>
        </div>
      </InviteStatePage>
    );
  }

  // Success state
  if (pageState === "success") {
    return (
      <InviteStatePage
        title={t.auth.welcomeAboard}
        description={t.auth.successfullyJoinedOrg.replace(
          "{organizationName}",
          inviteDetails?.organizationName || ""
        )}
        icon={<CheckCircle className="h-6 w-6" />}
        iconClassName="bg-green-100 text-green-600"
      >
        <div className="flex justify-center rounded-xl border border-green-200 bg-green-50 px-4 py-3">
          <Loader2 className="h-5 w-5 animate-spin text-green-700" aria-hidden />
        </div>
      </InviteStatePage>
    );
  }

  // Needs signup state
  if (pageState === "needs_signup" && inviteDetails) {
    return (
      <InviteStatePage
        title={
          <span className="block break-words [overflow-wrap:anywhere]">
            {t.auth.invitationHeading.replace("{organizationName}", inviteDetails.organizationName)}
          </span>
        }
        description={t.auth.invitationSubtitle}
      >
        <div className="space-y-5">
          {invitationSummary}

          <div className="space-y-3">
            <Button onClick={handleSignUpRedirect} className={AUTH_PRIMARY_ACTION_CLASSES}>
              {t.auth.createAccount}
            </Button>
            <p className="text-center text-[13.5px] text-muted-foreground">
              {t.auth.invitationAlreadyHasAccount}{" "}
              <Link
                to="/login"
                state={{ returnUrl: `/invite/accept?token=${encodeURIComponent(token || "")}` }}
                replace
                className={`${AUTH_LINK_CLASSES} underline underline-offset-4`}
              >
                {t.auth.invitationLogin}
              </Link>
            </p>
          </div>

          {invitationExpiryFooter}
        </div>
      </InviteStatePage>
    );
  }

  // Invite details state (logged in, ready to accept)
  if ((pageState === "invite_details" || pageState === "accepting") && inviteDetails) {
    return (
      <InviteStatePage title={t.auth.acceptInvitation} description={t.auth.invitedToJoinOrg}>
        <div className="space-y-5">
          {invitationSummary}

          <Button
            onClick={handleAcceptInvite}
            className={AUTH_PRIMARY_ACTION_CLASSES}
            disabled={pageState === "accepting"}
          >
            {pageState === "accepting" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t.auth.accepting}
              </>
            ) : (
              t.auth.acceptInvitation
            )}
          </Button>

          {invitationExpiryFooter}
        </div>
      </InviteStatePage>
    );
  }

  // Fallback
  return null;
}
